import { OktaAuth, OktaAuthOptions } from '@okta/okta-auth-js';

const defaultOktaConfig: Partial<OktaAuthOptions> = {
  clientId: 'okta_client_id',
  issuer: 'https://redislabs.com',
  redirectUri: window.location.origin,
  scopes: ['openid', 'email', 'profile'],
  pkce: true,
  postLogoutRedirectUri: `${window.location.origin}/#/logout`
};

export class SmOktaAuth {
  static oktaAuth: OktaAuth;

  constructor(options: OktaAuthOptions) {
    SmOktaAuth.oktaAuth = new OktaAuth(options);
  }

  static getInstance(options?: Partial<OktaAuthOptions>) {
    if (!SmOktaAuth.oktaAuth) {
      if (!options) {
        throw new Error('Okta auth options are required for initializing okta');
      }

      const { clientId, issuer } = options;

      const overrides = {
        clientId: clientId || defaultOktaConfig.clientId,
        issuer: issuer || defaultOktaConfig.issuer
      };

      // eslint-disable-next-line no-new
      new SmOktaAuth({
        ...defaultOktaConfig,
        ...overrides
      });
    }

    return SmOktaAuth.oktaAuth;
  }
}

export const closeOktaSession = async () => {
  const oktaAuth = SmOktaAuth.getInstance();
  let hasTokens = false;

  try {
    const tokens = await oktaAuth.tokenManager.getTokens();

    hasTokens = Object.keys(tokens).length > 0;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[closeOktaSession] Failed to get tokens:', error);
  }

  if (hasTokens) {
    try {
      await oktaAuth.revokeAccessToken();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[closeOktaSession] Failed to revoke access token:', error);
    }

    try {
      await oktaAuth.revokeRefreshToken();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[closeOktaSession] Failed to revoke refresh token:', error);
    }

    try {
      oktaAuth.tokenManager.clear();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[closeOktaSession] Failed to clear tokens:', error);
    }
  }

  // Check session status before closing
  // Note: session.get() returns {status: 'INACTIVE'} when no session exists (handles 404 gracefully)
  // However, it may throw if third-party cookies are blocked
  let session: { status: string } | undefined;

  try {
    session = (await oktaAuth.session.get()) as { status: string } | undefined;
  } catch (error) {
    // session.get() may fail due to third-party cookie blocking - nothing to close
    // eslint-disable-next-line no-console
    console.error('[closeOktaSession] Failed to get session status:', error);

    return;
  }

  // Only close if session is ACTIVE, skip if INACTIVE (nothing to close)
  if (session?.status === 'ACTIVE') {
    try {
      await oktaAuth.closeSession();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[closeOktaSession] Failed to close session:', error);
    }
  }
};
